import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { customersApi } from '../api/endpoints';
import { useAuthStore } from '../stores/auth.store';
import type { Customer } from '@motra/shared';

export function useCustomers(shopId?: string, keyword?: string) {
  const shop = useAuthStore((s) => s.shop);
  const activeShopId = shopId ?? shop?.id;

  return useQuery({
    queryKey: ['customers', activeShopId, keyword],
    queryFn: () => customersApi.list(activeShopId!, { keyword }),
    enabled: !!activeShopId,
    select: (res) => res.data ?? [],
  });
}

export function useUpsertCustomer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Partial<Customer> & { shopid?: string }) =>
      customersApi.upsert(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
    },
  });
}

export function useDeleteCustomer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (customerId: string) => customersApi.delete(customerId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
    },
  });
}

export function useCustomerRemarks(customerId?: string) {
  return useQuery({
    queryKey: ['customer-remarks', customerId],
    queryFn: () => customersApi.getRemarks(customerId!),
    enabled: !!customerId,
    select: (res) => res.data ?? [],
  });
}

export function useAddRemark() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ customerId, remark }: { customerId: string; remark: string }) =>
      customersApi.addRemark(customerId, remark),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['customer-remarks', variables.customerId] });
    },
  });
}

export function useCustomerHistory(customerId?: string) {
  return useQuery({
    queryKey: ['customer-history', customerId],
    queryFn: () => customersApi.getHistory(customerId!),
    enabled: !!customerId,
    select: (res) => res.data ?? [],
  });
}

export function useCustomerCredits(customerId?: string) {
  return useQuery({
    queryKey: ['customer-credits', customerId],
    queryFn: () => customersApi.getCredits(customerId!),
    enabled: !!customerId,
    select: (res) => res.data,
  });
}
